import { defineStore } from 'pinia'

type CacheEntry = { status: 'fetching' | 'success' | 'error'; expire: number; value?: unknown }

/**
 * TTL cache with concurrent-request de-duplication.
 *
 * Setup style is used here not for composables but for the non-reactive `inflight` Map: it de-dupes
 * concurrent fetches of the same key within one SSR request (global is loaded by page + navigation +
 * seo at once) and keeps Promises out of reactive state (Pinia would proxy them). The `caches` ref only
 * ever holds serializable status/expire/value, so SSR -> client hydration is safe.
 */
export const useCacheStore = defineStore('cache', () => {
  const caches = ref<Record<string, CacheEntry>>({})
  const inflight = new Map<string, Promise<unknown>>()

  /**
   * Return cached data while valid, otherwise fetch (de-duping concurrent calls for the same key).
   * `key` uniquely identifies the data, `fetcher` resolves it, `ttl` is the freshness window in seconds.
   */
  async function fetchCached<T>({ key, ttl, fetcher, fallback = undefined }: { key: string; ttl?: number; fetcher: () => Promise<T>; fallback?: T }): Promise<T> {
    const entry = caches.value[key]
    if (entry && ['success', 'error'].includes(entry.status) && new Date().getTime() <= entry.expire) {
      return entry.value as T
    }

    if (inflight.has(key)) {
      return inflight.get(key) as Promise<T>
    }

    const promise = (async () => {
      try {
        const value = await fetcher()
        caches.value[key] = { status: 'success', expire: new Date().getTime() + (ttl || 300) * 1000, value }
      } catch (error) {
        console.error('Error during "cache/fetch"', error)

        /*
         * In development a failing fetcher must be loud: rethrow instead of handing back `fallback`, which
         * is indistinguishable from a legitimately empty result. Drop the entry as well, so the next call
         * retries rather than replaying a cached failure in silence. In production the fallback stands, so
         * one broken upstream does not take the page down with it.
         */
        if (import.meta.dev) {
          delete caches.value[key]
          throw error
        }

        // Cache errors for half the ttl so a failing upstream is retried sooner than a success is refreshed.
        caches.value[key] = { status: 'error', expire: new Date().getTime() + (ttl || 300) * 500, value: fallback }
      } finally {
        inflight.delete(key)
      }
      return caches.value[key].value as T
    })()

    inflight.set(key, promise)
    caches.value[key] = { status: 'fetching', expire: 0 }

    return promise
  }

  return { caches, fetch: fetchCached }
})
